/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, { useEffect, useRef } from 'react';
import type { BaseFormField, BaseFormFieldType, FormErrors } from './form-contract';
import type { GuidedLearningRuntimeCapability } from './guided-learning.contract';
import './FormBuilder.css';

export interface FormField extends BaseFormField {
    /** Arama terimini server-side seçenek sağlayıcısına iletir. */
    onSearchChange?: (value: string) => void;
    searchPlaceholder?: string;
    noResultsText?: string;
    showSelectAll?: boolean;
    selectedCountLabel?: (count: number) => string;
}


export type LayoutFormField = FormField & {
    __autoActiveTop?: boolean;
};


const ACTIVE_SWITCH_FIELD_NAMES = new Set(['isactive', 'active']);

const SENSITIVE_LEARNING_FIELD_TYPES = new Set<BaseFormFieldType>(['password', 'file']);

function FileControl({ selectedValue, buttonLabel, ...props }: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'type'> & { selectedValue: unknown; buttonLabel?: string }) {
    const input = useRef<HTMLInputElement>(null);
    // A file value cannot be assigned programmatically. Clear the native
    // selection when the owning form clears it or replaces it with a managed
    // reference, so selecting the same file again can emit a change event.
    // Upload consumers may keep an unchanged reference while rerendering busy
    // state; checking each render also covers that controlled ownership model.
    useEffect(() => {
        const hasFiles = typeof File !== 'undefined' && (selectedValue instanceof File
            || (props.multiple && Array.isArray(selectedValue) && selectedValue.length > 0 && selectedValue.every(value => value instanceof File)));
        if (input.current?.value && !hasFiles) input.current.value = '';
    });
    if (!buttonLabel) return <input {...props} type="file" ref={input} />;
    const files = typeof File !== 'undefined'
        ? (selectedValue instanceof File ? [selectedValue] : Array.isArray(selectedValue) ? selectedValue.filter(value => value instanceof File) : [])
        : [];
    const names = files.map(file => file.name).join(', ');
    return <div className="form-file-picker">
        <span className={`form-file-picker-trigger btn-secondary${props.disabled ? ' is-disabled' : ''}${props['aria-invalid'] ? ' input-error' : ''}`}>
            <span aria-hidden="true">{buttonLabel}</span>
            <input {...props} className="form-file-picker-input" type="file" ref={input} />
        </span>
        {names && <span className="form-file-picker-name" title={names} aria-hidden="true">{names}</span>}
    </div>;
}

export function getFieldLearningCapabilities(field: FormField): GuidedLearningRuntimeCapability[] {
    if (!field.learningTargetId || SENSITIVE_LEARNING_FIELD_TYPES.has(field.type)) return [];
    if (field.type === 'select' || field.type === 'multiselect' || field.type === 'radio') return ['FOCUS', 'SELECT'];
    if (field.type === 'checkbox') return ['FOCUS', 'ACTIVATE'];
    if (field.type === 'dateRange') return ['FOCUS'];
    return ['FOCUS', 'SET_VALUE'];
}

const isActiveSwitchField = (field: FormField): boolean =>
    field.type === 'checkbox' && ACTIVE_SWITCH_FIELD_NAMES.has(field.name.toLowerCase());

export const buildLayoutFields = (fields: FormField[]): LayoutFormField[] => {
    fields = fields.filter((field) => !field.hidden);
    const activeIndex = fields.findIndex(isActiveSwitchField);
    if (activeIndex === -1) return fields;

    const activeField = fields[activeIndex];
    const otherFields = fields.filter((_, index) => index !== activeIndex);
    const promotedActiveField: LayoutFormField = {
        ...activeField,
        colSpan: 4,
        __autoActiveTop: true,
    };

    if (otherFields.length === 0) return [promotedActiveField];

    const [firstField, ...remainingFields] = otherFields;
    const firstColSpan = firstField.colSpan ?? 12;
    const normalizedFirstField: LayoutFormField =
        firstColSpan > 8
            ? { ...firstField, colSpan: 8 }
            : firstField;

    return [normalizedFirstField, promotedActiveField, ...remainingFields];
};


const isDecimalNumberField = (field: FormField): boolean => {
    if (field.type === 'money') return true;
    if (field.type !== 'number') return false;
    if (field.step === undefined || field.step === null) return false;
    const s = String(field.step).trim();
    if (!s) return false;
    // step 1 ise integer gibi davran
    if (s === '1' || s === '1.0' || s === '1,0') return false;
    // 0.01, 0,5 vb -> decimal
    return s.includes('.') || s.includes(',') || (!Number.isNaN(Number(s)) && !Number.isInteger(Number(s)));
};

const normalizeDecimalInput = (raw: string, allowNegative: boolean): string => {
    // Sadece rakam, tek bir ayırıcı (.,) ve opsiyonel '-' koru
    let v = raw.replace(/[^\d.,-]/g, '');
    if (!allowNegative) {
        v = v.replace(/-/g, '');
    } else {
        // '-' sadece en başta kalsın
        v = v.replace(/(?!^)-/g, '');
    }

    // İlk görülen ayırıcıyı tut, diğerlerini kaldır
    const firstSepIdx = v.search(/[.,]/);
    if (firstSepIdx !== -1) {
        const before = v.slice(0, firstSepIdx);
        const sep = v[firstSepIdx];
        const after = v.slice(firstSepIdx + 1).replace(/[.,]/g, '');
        v = `${before}${sep}${after}`;
    }

    return v;
};


export interface FormControlContext {
    field: FormField;
    fieldId: string;
    fieldValues: Record<string, any>;
    handleFieldChange: (name: string, value: any) => void;
    isError: boolean;
    fieldDescribedBy?: string;
    learningTargetId?: string;
}

export interface FormFieldGridProps {
    layout?: 'grid' | 'settings';
    fields: LayoutFormField[];
    values: Record<string, any>;
    onChange: (name: string, value: any) => void;
    onPatch: (values: Record<string, any>) => void;
    idPrefix?: string;
    errors?: FormErrors;
    /** Host supplies directory/date/select controls without sharing its API or session. */
    renderControl?: (context: FormControlContext) => React.ReactNode;
}

export function FormFieldGrid({
    layout = 'grid', fields: fieldsToRender, values: fieldValues, onChange: handleFieldChange,
    onPatch: patchValues, idPrefix = '', errors = {}, renderControl,
}: FormFieldGridProps) {
    if (!renderControl && fieldsToRender.some((field) => ['select', 'multiselect', 'datetime'].includes(field.type))) {
        throw new Error('FORM_CONTROL_RENDERER_REQUIRED');
    }
    return (
        <div className={layout === 'settings' ? 'form-row-grid qp-settings-fields' : 'form-row-grid'}>
            {fieldsToRender.map((field) => {
                    const colSpan = field.colSpan || 12;
                    const isError = !!errors[field.name];
                    const fieldId = `${idPrefix}field-${field.name}`;
                    const fieldDescriptionId = field.description ? `${idPrefix}field-description-${field.name}` : undefined;
                    const fieldErrorId = isError ? `${idPrefix}field-error-${field.name}` : undefined;
                    const fieldDescribedBy = [fieldDescriptionId, fieldErrorId].filter(Boolean).join(' ') || undefined;
                    const isDecimal = isDecimalNumberField(field);
                    const allowNegative = field.min !== undefined ? field.min < 0 : true;
                    const useSwitchPresentation =
                        field.presentationMode === 'switch' || (layout === 'settings' && field.type === 'checkbox') ||
                        (field.type === 'checkbox' && ACTIVE_SWITCH_FIELD_NAMES.has(field.name.toLowerCase()));
                    const learningCapabilities = getFieldLearningCapabilities(field);
                    const learningTargetId = learningCapabilities.length > 0
                        ? field.learningTargetId?.trim()
                        : undefined;

                    return (
                        <div
                            key={field.name}
                            className={`form-col col-${colSpan}${field.__autoActiveTop ? ' form-col-active-top' : ''}`}
                        >
                            <div
                                className="form-group"
                                data-qp-learning-id={learningTargetId}
                                data-qp-learning-capabilities={learningCapabilities.length > 0 ? learningCapabilities.join(' ') : undefined}
                            >
                                {field.type !== 'checkbox' && (
                                    <label id={`${fieldId}-label`} htmlFor={field.type === 'radio' ? `${fieldId}-option-0` : fieldId}>
                                        {field.label}
                                        {field.required && <span className="required-star">*</span>}
                                    </label>
                                )}

                                {/* TEXT / NUMBER / EMAIL / DATE INPUT */}
                                {renderControl?.({ field, fieldId, fieldValues, handleFieldChange, isError, fieldDescribedBy, learningTargetId })}

                                {field.type === 'radio' && (
                                    <div id={fieldId} className="form-radio-group" role="radiogroup"
                                        aria-labelledby={`${fieldId}-label`} aria-describedby={fieldDescribedBy}
                                        aria-required={field.required || undefined} aria-invalid={isError || undefined}>
                                        {(field.options ?? []).map((option, index) => (
                                            <label key={`${typeof option.value}:${String(option.value)}`} className="form-radio-option">
                                                <input id={`${fieldId}-option-${index}`} type="radio" name={fieldId}
                                                    value={String(option.value)} checked={fieldValues[field.name] === option.value}
                                                    required={field.required} disabled={field.disabled || option.disabled}
                                                    onChange={() => { if (!field.disabled && !option.disabled) handleFieldChange(field.name, option.value); }} />
                                                <span>{option.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}

                                {(field.type === 'text' || field.type === 'tel' || field.type === 'number' || field.type === 'money' || field.type === 'email' || field.type === 'password' || field.type === 'date') && (
                                    <input
                                        id={fieldId}
                                        // Decimal number'larda type=number yerine text kullan (virgül/nokta desteği)
                                        type={field.type === 'number' || field.type === 'money' ? (isDecimal ? 'text' : 'number') : field.type}
                                        inputMode={field.type === 'number' || field.type === 'money' ? (isDecimal ? 'decimal' : 'numeric') : undefined}
                                        pattern={field.type === 'number' || field.type === 'money'
                                            ? (isDecimal ? "[0-9]*[\\.,]?[0-9]*" : field.regex)
                                            : field.regex}
                                        value={fieldValues[field.name] === undefined ? '' : fieldValues[field.name]}
                                        onChange={(e) => {
                                            if ((field.type === 'number' || field.type === 'money') && isDecimal) {
                                                const normalized = normalizeDecimalInput(e.target.value, allowNegative);
                                                handleFieldChange(field.name, normalized);
                                                return;
                                            }
                                            handleFieldChange(field.name, e.target.value);
                                        }}
                                        placeholder={field.placeholder}
                                        required={field.required}
                                        disabled={field.disabled}
                                        min={field.min}
                                        max={field.max}
                                        step={field.step}
                                        minLength={field.type === 'number' || field.type === 'money' || field.type === 'date' ? undefined : field.minLength}
                                        maxLength={field.type === 'number' || field.type === 'money' || field.type === 'date' ? undefined : field.maxLength}
                                        spellCheck={field.spellCheck}
                                        autoComplete={field.autoComplete}
                                        aria-invalid={isError || undefined}
                                        aria-describedby={fieldDescribedBy}
                                        className={`form-input ${isError ? 'input-error' : ''}`}
                                    />
                                )}

                                {field.type === 'file' && (
                                    <FileControl
                                        id={fieldId}
                                        selectedValue={fieldValues[field.name]}
                                        buttonLabel={field.fileButtonLabel}
                                        accept={field.accept}
                                        multiple={field.multiple}
                                        onChange={(event) => handleFieldChange(field.name, field.multiple ? Array.from(event.target.files ?? []) : event.target.files?.[0] ?? null)}
                                        required={field.required}
                                        disabled={field.disabled}
                                        aria-invalid={isError || undefined}
                                        aria-describedby={fieldDescribedBy}
                                        className={`form-input ${isError ? 'input-error' : ''}`}
                                    />
                                )}

                                {/* TEXTAREA */}
                                {field.type === 'textarea' && (
                                    <textarea
                                        id={fieldId}
                                        value={fieldValues[field.name] === undefined ? '' : fieldValues[field.name]}
                                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                        placeholder={field.placeholder}
                                        required={field.required}
                                        disabled={field.disabled}
                                        minLength={field.minLength}
                                        maxLength={field.maxLength}
                                        spellCheck={field.spellCheck}
                                        aria-invalid={isError || undefined}
                                        aria-describedby={fieldDescribedBy}
                                        className={`form-input form-textarea ${isError ? 'input-error' : ''}`}
                                        rows={field.rows ?? 3}
                                    />
                                )}

                                {/* RICHTEXT: uydu projelerde HTML editörü olmadan uzun metin (textarea) */}
                                {field.type === 'richtext' && (
                                    <textarea
                                        id={fieldId}
                                        value={fieldValues[field.name] === undefined ? '' : fieldValues[field.name]}
                                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                        placeholder={field.placeholder}
                                        required={field.required}
                                        disabled={field.disabled}
                                        minLength={field.minLength}
                                        maxLength={field.maxLength}
                                        spellCheck={field.spellCheck}
                                        aria-invalid={isError || undefined}
                                        aria-describedby={fieldDescribedBy}
                                        className={`form-input form-textarea ${isError ? 'input-error' : ''}`}
                                        rows={field.rows ?? 6}
                                    />
                                )}

                                {/* TARİH ARALIĞI: iki native date input (startField / endField) */}
                                {field.type === 'dateRange' && field.startField && field.endField && (
                                    <div className="form-date-range-inputs" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <input
                                            type="date"
                                            id={`${idPrefix}field-${field.startField}`}
                                            value={fieldValues[field.startField] === undefined ? '' : fieldValues[field.startField]}
                                            onChange={(e) => patchValues({ [field.startField!]: e.target.value })}
                                            disabled={field.disabled}
                                            required={field.required}
                                            className={`form-input ${isError ? 'input-error' : ''}`}
                                            aria-label={(field.placeholder || field.label) + ' — start'}
                                        />
                                        <span style={{ opacity: 0.7 }}>–</span>
                                        <input
                                            type="date"
                                            id={`${idPrefix}field-${field.endField}`}
                                            value={fieldValues[field.endField] === undefined ? '' : fieldValues[field.endField]}
                                            onChange={(e) => patchValues({ [field.endField!]: e.target.value })}
                                            disabled={field.disabled}
                                            required={field.required}
                                            className={`form-input ${isError ? 'input-error' : ''}`}
                                            aria-label={(field.placeholder || field.label) + ' — end'}
                                        />
                                    </div>
                                )}

                                {field.type === 'checkbox' && (
                                    <label
                                        className={`checkbox-label ${useSwitchPresentation ? 'checkbox-switch-label' : ''}`}
                                    >
                                        <input
                                            id={fieldId}
                                            type="checkbox"
                                            className="qubepro-checkbox"
                                            role={useSwitchPresentation ? 'switch' : undefined}
                                            checked={!!fieldValues[field.name]}
                                            onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                                            disabled={field.disabled}
                                        />
                                        {useSwitchPresentation && (
                                            <span className="checkbox-switch-control" aria-hidden="true" />
                                        )}
                                        <span className="checkbox-label-text">{field.label}</span>
                                    </label>
                                )}

                                {field.description && (
                                    <div id={fieldDescriptionId} className="form-field-description">
                                        {field.description}
                                    </div>
                                )}

                                {/* Hata Mesajı */}
                                {isError && (
                                    <div id={fieldErrorId} className="field-error-message" role="alert">
                                        {errors[field.name]}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
            })}
        </div>
    );
}
